import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import useSurveyLocale from '@aytm/app/swidget/base/modules/locale/useSurveyLocale'
import useTimeline from '@aytm/app/swidget/base/modules/timeline/useTimeline'
import useViewport from '@aytm/app/swidget/base/modules/viewport/useViewport.ts'
import { IImageAsset } from '@aytm/app/swidget/base/modules/images/types'
import { getBigAssetPath } from '@aytm/lib/common/assets'
import '@aytm/app/swidget/base/questions/Ecommerce/Ecommerce/Store/ModelViewer.ts'
import {
  ASSETS_TYPES,
  assetsCollectionElement,
  IEcommerceAnswerSchema,
  IEcommerceVideoAsset,
  IFileAsset
} from '../../types.ts'
import useAssetsCollectionBuilder from '../../hooks/useAssetsCollectionBuilder.ts'
import { assetDialogEvent } from '../../utils/assetDialogEvent.ts'
import { defaultAssetIndex } from '../../utils/defaultAssetIndex.ts'
import { getAssetType } from '../../utils/getAssetType.ts'
import AssetThumb from './AssetThumb.tsx'
import AssetsModal from './AssetsModal.tsx'
import style from './Gallery.module.scss'

interface IGalleryProps {
  answer: IEcommerceAnswerSchema
}

// Detail-page asset column: the main asset is a still preview (no zoom, no playback) except the
// model, which rotates in place; the magnifier or a click on a photo/video opens the full viewer.
const Gallery = ({ answer }: IGalleryProps) => {
  const { t } = useSurveyLocale()
  const timeline = useTimeline()
  const viewport = useViewport()
  const assetsCollection = useAssetsCollectionBuilder(answer)
  const [selectedIndex, setSelectedIndex] = useState<number>(() => defaultAssetIndex(assetsCollection))
  const [modalIndex, setModalIndex] = useState<number | null>(null)
  const stripRef = useRef<HTMLDivElement>(null)
  const thumbRefs = useRef<(HTMLButtonElement | null)[]>([])
  const trackRef = useRef<HTMLDivElement>(null)
  const slideRefs = useRef<(HTMLDivElement | null)[]>([])

  const selectedAsset = assetsCollection[selectedIndex]

  useEffect(() => {
    timeline.log({
      type: 'show_asset',
      ref: answer.id,
      target: selectedIndex.toString(),
      context: (getAssetType(assetsCollection[selectedIndex]) ?? '').toLowerCase()
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIndex])

  // The row opens at the start; a starred asset further along must be the slide on screen,
  // not only the ticked thumb. Before paint, so the first slide never flashes.
  useLayoutEffect(() => {
    const track = trackRef.current
    const slide = slideRefs.current[selectedIndex]
    if (!track || !slide || 0 === selectedIndex) return
    track.scrollLeft = slide.offsetLeft - (slideRefs.current[0]?.offsetLeft ?? 0)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Scroll the strip only when the thumb sits outside it, measured as rect deltas (the strip is
  // not the thumbs' offset parent).
  const revealThumb = useCallback((index: number) => {
    const strip = stripRef.current
    const thumb = thumbRefs.current[index]
    if (!strip || !thumb) return
    const box = strip.getBoundingClientRect()
    const item = thumb.getBoundingClientRect()
    if (item.left < box.left) {
      strip.scrollTo({ left: strip.scrollLeft - (box.left - item.left), behavior: 'smooth' })
    } else if (item.right > box.right) {
      strip.scrollTo({ left: strip.scrollLeft + (item.right - box.right), behavior: 'smooth' })
    }
  }, [])

  const onStep = (direction: 1 | -1) => {
    const next = selectedIndex + direction
    if (next < 0 || next >= assetsCollection.length) return
    setSelectedIndex(next)
    revealThumb(next)
  }

  const openModal = (index: number = selectedIndex) => {
    const asset = assetsCollection[index]
    if (!asset) return
    timeline.log({ ref: answer.id, type: assetDialogEvent('open', asset) })
    setModalIndex(index)
  }

  // The still preview of an asset; the model rotates in place and never opens the viewer by itself.
  const assetView = (asset: assetsCollectionElement, onOpen: () => void) => {
    const type = getAssetType(asset)
    if (ASSETS_TYPES.IMAGE === type) {
      return (
        <div
          className={style.image}
          role="img"
          aria-label={(asset as IImageAsset).alt_text || t('questions', 'img', 'noAlt')}
          style={{ backgroundImage: `url('${getBigAssetPath(asset as IImageAsset)}')` }}
          onClick={onOpen}
        />
      )
    }
    if (ASSETS_TYPES.VIDEO === type) {
      return (
        <div
          className={style.image}
          role="presentation"
          style={{ backgroundImage: `url('${(asset as IEcommerceVideoAsset).video.thumbnail}')` }}
          onClick={onOpen}
        />
      )
    }
    if (ASSETS_TYPES.MODEL === type) {
      return (
        <div className={style.model}>
          <model-viewer
            src={(asset as IFileAsset).file_url}
            camera-controls
            touch-action="pan-y"
            interaction-prompt="none"
          />
        </div>
      )
    }
    return null
  }

  const modal = null !== modalIndex && (
    <AssetsModal
      answer={answer}
      assetsCollection={assetsCollection}
      index={modalIndex}
      onClose={() => setModalIndex(null)}
    />
  )

  const goToSlide = (index: number) => {
    const track = trackRef.current
    const slide = slideRefs.current[index]
    setSelectedIndex(index)
    if (!track || !slide) return
    track.scrollTo({ left: slide.offsetLeft - (slideRefs.current[0]?.offsetLeft ?? 0), behavior: 'smooth' })
  }

  // The app's gallery: the assets in a row with the next one peeking in, the thumbs under it. The row
  // does not swipe: the thumbs are the only way along it, and a drag on the model only rotates it.
  if (viewport.mobile) {
    return (
      <div className={style.galleryMobile}>
        <div className={style.track} ref={trackRef}>
          {assetsCollection.length > 0 ? (
            assetsCollection.map((asset, index) => (
              <div
                className={style.slide}
                key={index}
                ref={node => {
                  slideRefs.current[index] = node
                }}
              >
                {assetView(asset, () => openModal(index))}
              </div>
            ))
          ) : (
            <div className={style.slide}>
              <div className={style.noImage} />
            </div>
          )}
        </div>
        {assetsCollection.length > 1 && (
          <div className={style.thumbs}>
            {assetsCollection.map((asset, index) => (
              <AssetThumb key={index} asset={asset} active={index === selectedIndex} onClick={() => goToSlide(index)} />
            ))}
          </div>
        )}
        {modal}
      </div>
    )
  }

  return (
    <div className={style.gallery}>
      <div className={style.stage}>
        {/* A drag on the model ends in a click, so the model rotates here and opens only from the magnifier. */}
        {selectedAsset && assetView(selectedAsset, () => openModal())}
        {!selectedAsset && <div className={style.noImage} />}
        {selectedAsset && (
          <button
            type="button"
            className={style.magnifier}
            onClick={() => openModal()}
            aria-label={t('questions', 'ec', 'tesco', 'zoomIn')}
          >
            <i className="pi pi-search-plus" />
          </button>
        )}
      </div>

      {assetsCollection.length > 1 && (
        <div className={style.strip}>
          <button
            type="button"
            className={style.arrow}
            disabled={0 === selectedIndex}
            onClick={() => onStep(-1)}
            aria-label={t('questions', 'ec', 'tesco', 'previous')}
          >
            <i className="pi pi-chevron-left" />
          </button>
          <div className={style.thumbs} ref={stripRef}>
            {assetsCollection.map((asset, index) => (
              <AssetThumb
                key={index}
                ref={node => {
                  thumbRefs.current[index] = node
                }}
                asset={asset}
                active={index === selectedIndex}
                onClick={() => setSelectedIndex(index)}
              />
            ))}
          </div>
          <button
            type="button"
            className={style.arrow}
            disabled={assetsCollection.length - 1 === selectedIndex}
            onClick={() => onStep(1)}
            aria-label={t('questions', 'ec', 'tesco', 'next')}
          >
            <i className="pi pi-chevron-right" />
          </button>
        </div>
      )}

      {modal}
    </div>
  )
}

export default Gallery
